import React, { useState } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid,
} from '../../../components/mui/Core/index'
import GenericNav from '../../../components/genericcomponents/navbars/GenericNav'
import DmsOrdersTable from './components/DmsOrdersTable/dms-orders-table'
import WmsOrdersTable from './components/WmsOrdersTable/wms-orders-table'
import TableSearch from '../../../components/TableSearch/table-search'
import useBrowserTabTitles from '../../../hooks/browserTabTitles/browser-tab-titles'
import PageTitle from '../../../components/PageTitle/page-title'
import Constants from '../../../constants/en.json'
import SelectClientDropdown from '../../../components/dropdowns/SelectClientDropdown/select-client-dropdown'
import ClientSelection from '../../../components/ClientSelection/client-selection'
import { userPermissionConstants } from '../../../constants/user-permission-constants'
import { hasUserPermission } from '../../../utils/user-utils'
import { sessionStorageKeyConstants } from '../../../constants/session-storage-key-constants'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import {
  DMS_ORDER_STATUS_OPTIONS,
  WMS_ORDER_AND_SHIPMENT_STATUS_OPTIONS,
} from '../../../constants/status'

function Orders() {
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const [selectedClient, setSelectedClient] = useState(
    JSON.parse(sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT)),
  )
  const dispatch = useDispatch()
  const currentClient = selectedClient ? selectedClient?.clientCode : authenticatedUser?.clientCode
  const dmsUser = selectedClient?.dmsFlag || authenticatedUser?.dms
  const isFetchingOrders = useSelector((state) => state.order.isFetchingOrders)
  const searchByOptions = [
    { id: 'poNumber', text: Constants.orders.PO_NUMBER, type: 'string' },
    { id: 'createdOn', text: Constants.orders.CREATED_ON, type: 'date' },
    { id: 'orderStatus', text: Constants.orders.STATUS, type: 'dropdown' },
    { id: 'customerName', text: Constants.orders.SHIP_TO_NAME, type: 'string' },
    { id: 'shipacct', text: Constants.orders.SHIP_TO_ACCOUNT, type: 'string' },
  ]
  if (dmsUser === 'Y') {
    searchByOptions.unshift({
      id: 'dmsOrderId',
      text: Constants.orders.ORDER,
      type: 'string',
    })
  }
  if (dmsUser === 'N') {
    searchByOptions.unshift({
      id: 'wmsOrderId',
      text: Constants.orders.ORDER,
      type: 'string',
    })
    searchByOptions.push({
      id: 'dateShipped',
      text: Constants.orders.DATE_SHIPPED,
      type: 'date',
    })
  }
  const [tableFilterValue, setTableFilterValue] = useState('')
  const [tableFilterBy, setTableFilterBy] = useState(searchByOptions[0].id)
  const hasSwitchClientPermission = hasUserPermission({
    permissionCode: userPermissionConstants.SWITCH_CLIENT,
    permissions: authenticatedUser?.permissions,
  })
  const browserTabTitle = selectedClient
    ? `${selectedClient?.clientShortName} - ${Constants.orders.ORDERS}`
    : Constants.orders.ORDERS
  useBrowserTabTitles(browserTabTitle)

  const returnSearch = (inputValue, selectedMenuItemIndex) => {
    const isInvalidStatusSearchLength =
      searchByOptions[selectedMenuItemIndex].id === 'orderStatus' &&
      inputValue?.length > 0 &&
      inputValue?.length < 3

    if (isInvalidStatusSearchLength) {
      dispatch(
        setSnackbar({
          message: Constants.tableSearch.errorMessages.AT_LEAST_3_CHARACTERS,
          severity: 'error',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      return
    }

    if (searchByOptions[selectedMenuItemIndex].type === 'date') {
      if (!inputValue) {
        setTableFilterValue('')
        return
      }

      const date = moment.utc(inputValue).format('L')
      const year = date.split('/')?.at(-1)

      if (date === Constants.orders.INVALID_DATE_MESSAGE || parseInt(year, 10) < 1900) {
        dispatch(
          setSnackbar({
            message: Constants.orders.INVALID_DATE_MESSAGE,
            severity: 'error',
            action: true,
            autoHideDuration: 3000,
          }),
        )
        return
      }

      setTableFilterValue(date)
    } else {
      setTableFilterValue(inputValue)
    }
    setTableFilterBy(searchByOptions[selectedMenuItemIndex].id)
  }

  const handleSelectClientDropdown = (client) => {
    setSelectedClient(client)
    returnSearch('', 0) // Reset the table filter
  }

  const tableSearchWrapperStyle = {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <GenericNav selected={Constants.orders.ORDERS} user='authenticated' />
      <Backdrop open={isFetchingOrders}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid size={{ xs: hasSwitchClientPermission ? 12 : 6 }}>
            <PageTitle title={Constants.orders.ORDERS} />
          </Grid>
          {hasSwitchClientPermission &&
            (!selectedClient ? (
              <ClientSelection
                handleSelectOption={handleSelectClientDropdown}
                message={Constants.orders.SELECT_CLIENT_MESSAGE}
              />
            ) : (
              <>
                <Grid size={{ xs: 6 }}>
                  <SelectClientDropdown
                    currentState={selectedClient}
                    handleSelectOption={handleSelectClientDropdown}
                  />
                </Grid>
                <Grid size={{ xs: 6 }} sx={tableSearchWrapperStyle}>
                  <TableSearch
                    key={selectedClient?.clientCode || 'clientCode'}
                    returnSearch={returnSearch}
                    searchByOptions={searchByOptions}
                    statusOptions={
                      dmsUser === 'Y'
                        ? DMS_ORDER_STATUS_OPTIONS
                        : WMS_ORDER_AND_SHIPMENT_STATUS_OPTIONS
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {dmsUser === 'Y' ? (
                    <DmsOrdersTable
                      filterBy={tableFilterBy}
                      filterValue={tableFilterValue}
                      selectedClient={currentClient}
                      searchByOptions={searchByOptions}
                    />
                  ) : (
                    <WmsOrdersTable
                      filterBy={tableFilterBy}
                      filterValue={tableFilterValue}
                      selectedClient={currentClient}
                      searchByOptions={searchByOptions}
                    />
                  )}
                </Grid>
              </>
            ))}
          {!hasSwitchClientPermission && (
            <>
              <Grid size={{ xs: 6 }} sx={tableSearchWrapperStyle}>
                <TableSearch
                  returnSearch={returnSearch}
                  searchByOptions={searchByOptions}
                  statusOptions={
                    dmsUser === 'Y'
                      ? DMS_ORDER_STATUS_OPTIONS
                      : WMS_ORDER_AND_SHIPMENT_STATUS_OPTIONS
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                {dmsUser === 'Y' ? (
                  <DmsOrdersTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    selectedClient={currentClient}
                    searchByOptions={searchByOptions}
                  />
                ) : (
                  <WmsOrdersTable
                    filterBy={tableFilterBy}
                    filterValue={tableFilterValue}
                    selectedClient={currentClient}
                    searchByOptions={searchByOptions}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Box>
  )
}

export default Orders
